import { useCallback, useEffect, useState } from "react";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { notifyEmail } from "./EmailNotifications";
import baseUrl from "../../../api/baseUrl";
import { PanelCard, SectionHeader } from "../components/accountUi";

type TemplateOption = { id: string; name: string; subject: string; body: string };

type PatientRow = { id: string; fullName: string; email: string; regNo: string };

type SendResult = {
  ok: boolean;
  message: string;
  failed?: { email: string; error?: string }[];
};

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

export default function EmailSendPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [credits, setCredits] = useState(0);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [audience, setAudience] = useState<"all" | "selected">("selected");
  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [totalWithEmail, setTotalWithEmail] = useState(0);
  const [selected, setSelected] = useState<Record<string, PatientRow>>({});
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  const loadTemplates = useCallback(() => {
    baseUrl
      .get("/client/account/email-templates")
      .then((res) => {
        const data = res?.data?.data;
        if (!data) return;
        setTemplates([...(data.custom ?? []), ...(data.automatic ?? [])]);
      })
      .catch(() => {});
  }, []);

  const loadSettings = useCallback(() => {
    baseUrl
      .get("/client/account/email-overview")
      .then((res) => {
        const settings = res?.data?.data?.settings;
        setEnabled(Boolean(settings?.enabled));
        setCredits(Number(settings?.credits) || 0);
      })
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    loadSettings();
    loadTemplates();
  }, [loadSettings, loadTemplates]);

  useEffect(() => {
    if (!enabled) return;
    setLoadingPatients(true);
    const timer = setTimeout(() => {
      baseUrl
        .get("/client/account/email-patients", { params: { search: search.trim() || undefined } })
        .then((res) => {
          setPatients(res?.data?.data?.patients ?? []);
          setTotalWithEmail(res?.data?.data?.total ?? 0);
        })
        .catch(() => setPatients([]))
        .finally(() => setLoadingPatients(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [enabled, search]);

  const togglePatient = (patient: PatientRow) => {
    setSelected((current) => {
      const next = { ...current };
      if (next[patient.id]) delete next[patient.id];
      else next[patient.id] = patient;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;
  const recipientCount = audience === "all" ? totalWithEmail : selectedCount;
  const template = templates.find((item) => item.id === templateId);
  const notEnoughCredits = recipientCount > credits;

  const send = () => {
    if (!template || recipientCount === 0) return;
    if (
      !window.confirm(
        `Send "${template.name}" to ${recipientCount} patient${recipientCount === 1 ? "" : "s"}?`
      )
    ) {
      return;
    }
    setSending(true);
    setResult(null);
    baseUrl
      .post("/client/account/email-send", {
        templateId,
        audience,
        patientIds: audience === "selected" ? Object.keys(selected) : undefined,
      })
      .then((res) => {
        const results = res?.data?.data?.results ?? [];
        setResult({
          ok: (res?.data?.data?.failed ?? 0) === 0,
          message: res?.data?.message || "Emails sent",
          failed: results.filter((item: any) => !item.sent),
        });
        const failed = res?.data?.data?.failed ?? 0;
        if (failed === 0) {
          setSelected({});
          notifyEmail("success", "Emails sent!", res?.data?.message || "Emails sent successfully.");
        } else {
          notifyEmail("warning", "Some emails not sent", res?.data?.message || `${failed} email(s) could not be sent.`);
        }
      })
      .catch((err) => {
        const message = errorMessageFrom(err, "Sending failed");
        setResult({ ok: false, message });
        notifyEmail("error", "Emails not sent", message);
      })
      .finally(() => {
        setSending(false);
        loadSettings();
        onChanged?.();
      });
  };

  return (
    <PanelCard className="overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
        <SectionHeader
          icon={ForwardToInboxOutlinedIcon}
          title="Send Email to Patients"
          subtitle="Pick a template and the patients to email"
        />
      </div>

      <div className="space-y-4 p-5">
        {enabled === false ? (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
            Email to patients is not turned on for your clinic. Contact Care32 support to turn it on.
          </div>
        ) : null}

        {enabled ? (
          <>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Template</span>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                onFocus={loadTemplates}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white sm:max-w-md"
              >
                <option value="">Choose a template…</option>
                {templates.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.id.startsWith("event:") ? " (automatic email)" : ""}
                  </option>
                ))}
              </select>
            </label>
            {template ? (
              <div className="rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                  Subject: <span className="font-normal">{template.subject}</span>
                </p>
                <p className="mt-1 line-clamp-4 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
                  {template.body}
                </p>
                <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                  {"{{patient_name}}, {{clinic_name}} and {{registration_number}}"} are filled for each
                  patient. Other placeholders stay as they are, so use templates without them here.
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-200">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={audience === "selected"}
                  onChange={() => setAudience("selected")}
                />
                Choose patients
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" checked={audience === "all"} onChange={() => setAudience("all")} />
                All patients with an email ({totalWithEmail})
              </label>
            </div>

            {audience === "selected" ? (
              <div className="rounded-2xl border border-slate-200/80 p-3 dark:border-slate-700/80">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email or reg no…"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
                <div className="mt-2 max-h-64 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-700/60">
                  {loadingPatients ? (
                    <p className="py-3 text-sm text-slate-500 dark:text-slate-400">Loading patients…</p>
                  ) : patients.length === 0 ? (
                    <p className="py-3 text-sm text-slate-500 dark:text-slate-400">
                      No patients with an email address{search.trim() ? " match your search" : ""}.
                    </p>
                  ) : (
                    patients.map((patient) => (
                      <label
                        key={patient.id}
                        className="flex cursor-pointer items-center gap-3 py-2 text-sm text-slate-700 dark:text-slate-200"
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(selected[patient.id])}
                          onChange={() => togglePatient(patient)}
                          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{patient.fullName || "—"}</span>
                          {patient.regNo ? (
                            <span className="ml-2 text-xs text-slate-400">{patient.regNo}</span>
                          ) : null}
                          <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                            {patient.email}
                          </span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {selectedCount} selected
                  {selectedCount ? (
                    <button
                      type="button"
                      onClick={() => setSelected({})}
                      className="ml-2 font-semibold text-primary hover:underline"
                    >
                      Clear
                    </button>
                  ) : null}
                </p>
              </div>
            ) : null}

            <button
              type="button"
              onClick={send}
              disabled={sending || !template || recipientCount === 0 || notEnoughCredits}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            >
              <SendOutlinedIcon sx={{ fontSize: 16 }} />
              {sending
                ? "Sending…"
                : `Send to ${recipientCount} patient${recipientCount === 1 ? "" : "s"}`}
            </button>
            <p
              className={`text-xs ${
                notEnoughCredits ? "text-rose-700 dark:text-rose-300" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {credits <= 0
                ? "Email tokens are finished. Buy email tokens or contact the Care32 team."
                : notEnoughCredits
                ? `Not enough email tokens: ${recipientCount} needed, ${credits} left. Buy email tokens or contact the Care32 team.`
                : `Email tokens left: ${credits} (1 token per email)`}
            </p>

            {result ? (
              <div
                className={`text-xs ${
                  result.ok ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"
                }`}
              >
                <p>{result.message}</p>
                {result.failed?.length ? (
                  <ul className="mt-1 list-disc pl-5">
                    {result.failed.slice(0, 10).map((item) => (
                      <li key={item.email}>
                        {item.email}: {item.error || "failed"}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PanelCard>
  );
}
